import React, { useCallback, useMemo } from 'react';
import {
    Alert,
    Box,
    Checkbox,
    CircularProgress,
    Divider,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { useTranslation } from 'react-i18next';
import useTicketMatrixData from '../../../hooks/useTicketMatrixData';
import { matrixHasExplicitEnabledFlags } from '../../../helpers/commissionMatrixUtils';

const hideBorderSx = { borderColor: 'transparent' };
const hidePseudoSx = { '&:before, &:after': { display: 'none' } };

const typeSelectSx = {
    minWidth: 54,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderRight: '1px solid #cbd5e1',
    bgcolor: '#f8fafc',
    color: '#64748b',
    ...hidePseudoSx,
    '& .MuiOutlinedInput-notchedOutline': hideBorderSx,
    '&:hover .MuiOutlinedInput-notchedOutline': hideBorderSx,
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': hideBorderSx,
    '& .MuiNativeSelect-select': {
        paddingTop: '5px',
        paddingBottom: '5px',
        paddingLeft: '12px',
        paddingRight: '28px',
        fontSize: '1.25rem',
        fontWeight: 500,
        color: '#1e293b',
    },
    '& .MuiNativeSelect-icon, & .MuiSelect-icon': {
        right: '6px',
        color: '#64748b',
        fontSize: '22px',
    },
    '& .MuiInputBase-root:before, & .MuiInputBase-root:after': { display: 'none' },
};

const valueFieldSx = {
    width: '100%',
    ...hidePseudoSx,
    '& .MuiOutlinedInput-root': {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        bgcolor: 'common.white',
    },
    '& .MuiOutlinedInput-notchedOutline': hideBorderSx,
    '&:hover .MuiOutlinedInput-notchedOutline': hideBorderSx,
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': hideBorderSx,
    '& .MuiInputBase-input': {
        py: '6px',
        px: 1.5,
        fontSize: '1.25rem',
        fontWeight: 500,
        color: '#1e293b',
    },
    '& .MuiInputBase-root:before, & .MuiInputBase-root:after': { display: 'none' },
};

const sellCheckboxSx = {
    p: 0.25,
    pl: 0,
    color: '#cbd5e1',
    '&.Mui-checked': { color: '#3b82f6' },
    '&:hover': { bgcolor: 'transparent' },
    '& .MuiSvgIcon-root': { fontSize: 22, borderRadius: '6px' },
};

const guestVisibleCheckboxSx = {
    ...sellCheckboxSx,
    '&.Mui-checked': { color: '#94a3b8' },
    '&.Mui-disabled': { color: '#94a3b8' },
};

const firstHeaderCellSx = {
    p: 2,
    fontSize: 13,
    fontWeight: 600,
    color: 'grey.500',
    width: '25%',
    textAlign: 'left',
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
};

const bodyRowSx = {
    '& td': { borderBottom: '1px solid', borderColor: 'grey.100' },
    '&:last-child td': { borderBottom: 'none' },
};

const typeLabelCellSx = { py: 2, pr: 2, px: 1, fontWeight: 600, color: 'grey.700' };

const tableContainerSx = { maxHeight: 400, overflow: 'auto', maxWidth: '100%', borderRadius: 2 };
const tableSx = { borderCollapse: 'separate', borderSpacing: 0 };
const tableHeadSx = { '& .MuiTableCell-root': { borderBottom: 'none' } };
const sectionBodySx = { p: 2, display: 'flex', flexDirection: 'column', gap: 1, width: '100%' };
const sectionTitleSx = {
    m: 0,
    fontSize: '1.25rem',
    lineHeight: 1.3,
    fontWeight: 600,
    color: 'text.primary',
};
const paperSx = { bgcolor: 'common.white', borderColor: 'grey.300', overflow: 'hidden' };

const optionHeaderCellSx = (option, ticketOptions, extraSx = {}) => ({
    p: 2,
    fontSize: 13,
    fontWeight: 600,
    color: 'grey.900',
    textAlign: 'left',
    ...extraSx,
    ...(option.id === ticketOptions[ticketOptions.length - 1]?.id
        ? { borderTopRightRadius: 8, borderBottomRightRadius: 8 }
        : {}),
});

const CommissionMatrix = ({ showId, commissionMatrix, setCommissionMatrix }) => {
    const { t } = useTranslation();
    const { ticketTypes, ticketOptions, loading, error } = useTicketMatrixData({ showId });

    const getMatrixEntry = useCallback(
        (ticketTypeId, option) => {
            const optionIdKey = String(option.id);
            return (
                commissionMatrix?.[ticketTypeId]?.[optionIdKey] ??
                commissionMatrix?.[ticketTypeId]?.[option.name]
            );
        },
        [commissionMatrix],
    );

    const getValue = useCallback(
        (ticketTypeId, option) => {
            return getMatrixEntry(ticketTypeId, option)?.value ?? '';
        },
        [getMatrixEntry],
    );

    const getType = useCallback(
        (ticketTypeId, option) => {
            return getMatrixEntry(ticketTypeId, option)?.type ?? 'percentage';
        },
        [getMatrixEntry],
    );

    const hasExplicitEnabled = useMemo(
        () => matrixHasExplicitEnabledFlags(commissionMatrix),
        [commissionMatrix],
    );

    const isCellGuestVisibleLocked = (ticketType, option) => {
        return !!ticketType?.guestVisible && option?.guestVisible !== false;
    };

    const isSellEnabled = useCallback(
        (ticketTypeId, ticketType, option) => {
            if (isCellGuestVisibleLocked(ticketType, option)) {
                return true;
            }
            if (!hasExplicitEnabled) {
                return true;
            }
            return getMatrixEntry(ticketTypeId, option)?.enabled === true;
        },
        [getMatrixEntry, hasExplicitEnabled, isCellGuestVisibleLocked],
    );

    const updateMatrixCell = useCallback(
        (ticketTypeId, option, patch) => {
            const optionIdKey = String(option.id);
            const currentEntry = getMatrixEntry(ticketTypeId, option);
            const currentEnabled = hasExplicitEnabled ? currentEntry?.enabled === true : true;
            setCommissionMatrix({
                ...commissionMatrix,
                [ticketTypeId]: {
                    ...commissionMatrix?.[ticketTypeId],
                    [optionIdKey]: {
                        type: currentEntry?.type ?? 'percentage',
                        value: currentEntry?.value ?? '',
                        enabled: currentEnabled,
                        ...patch,
                    },
                },
            });
        },
        [commissionMatrix, setCommissionMatrix, getMatrixEntry, hasExplicitEnabled],
    );

    const handleValueChange = useCallback(
        (ticketTypeId, option, value) => {
            updateMatrixCell(ticketTypeId, option, {
                value: value === '' ? '' : Number(value),
            });
        },
        [updateMatrixCell],
    );

    const handleTypeChange = useCallback(
        (ticketTypeId, option, type) => {
            updateMatrixCell(ticketTypeId, option, { type });
        },
        [updateMatrixCell],
    );

    const ensureExplicitEnabledFlags = useCallback(
        (matrix) => {
            if (hasExplicitEnabled) return matrix;
            const result = { ...matrix };
            for (const type of ticketTypes) {
                const typeId = String(type.id);
                result[typeId] = { ...result[typeId] };
                for (const option of ticketOptions) {
                    const optionId = String(option.id);
                    const existing = result[typeId]?.[optionId] ?? result[typeId]?.[option.name];
                    result[typeId][optionId] = {
                        type: existing?.type ?? 'percentage',
                        value: existing?.value ?? '',
                        enabled: true,
                    };
                }
            }
            return result;
        },
        [hasExplicitEnabled, ticketTypes, ticketOptions],
    );

    const handleEnabledChange = useCallback(
        (ticketTypeId, option, enabled) => {
            const base = ensureExplicitEnabledFlags(commissionMatrix);
            const optionIdKey = String(option.id);
            setCommissionMatrix({
                ...base,
                [ticketTypeId]: {
                    ...base[ticketTypeId],
                    [optionIdKey]: {
                        ...base[ticketTypeId]?.[optionIdKey],
                        enabled,
                    },
                },
            });
        },
        [commissionMatrix, setCommissionMatrix, ensureExplicitEnabledFlags],
    );

    const getRowEnabledState = useCallback(
        (ticketTypeId, ticketType) => {
            const allLocked = ticketOptions.every((option) =>
                isCellGuestVisibleLocked(ticketType, option),
            );
            if (allLocked) {
                return { checked: true, indeterminate: false, disabled: true };
            }

            const enabledCount = ticketOptions.filter((option) =>
                isSellEnabled(ticketTypeId, ticketType, option),
            ).length;

            return {
                checked: enabledCount === ticketOptions.length && ticketOptions.length > 0,
                indeterminate: enabledCount > 0 && enabledCount < ticketOptions.length,
                disabled: false,
            };
        },
        [ticketOptions, isSellEnabled, isCellGuestVisibleLocked],
    );

    const handleRowEnabledChange = useCallback(
        (ticketType, enabled) => {
            const base = ensureExplicitEnabledFlags(commissionMatrix);
            const ticketTypeId = ticketType.id;
            const nextRow = ticketOptions.reduce((acc, option) => {
                const optionIdKey = String(option.id);
                acc[optionIdKey] = {
                    ...base[ticketTypeId]?.[optionIdKey],
                    type: getType(ticketTypeId, option),
                    value: getValue(ticketTypeId, option),
                    enabled,
                };
                return acc;
            }, {});

            setCommissionMatrix({
                ...base,
                [ticketTypeId]: {
                    ...base[ticketTypeId],
                    ...nextRow,
                },
            });
        },
        [
            commissionMatrix,
            setCommissionMatrix,
            ticketOptions,
            getType,
            getValue,
            ensureExplicitEnabledFlags,
        ],
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{t('common.errorLoadingData')}</Alert>;
    }

    if (!ticketTypes?.length || !ticketOptions?.length) {
        return <Alert severity="info">{t('commissionGroups.noTicketTypesOrOptions')}</Alert>;
    }

    return (
        <Box
            sx={{
                minWidth: 0,
                overflowX: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                width: '100%',
            }}
        >
            {/* Commission Values */}
            <Paper variant="outlined" sx={paperSx}>
                <Box sx={{ px: 2, py: 1.5, textAlign: 'left' }}>
                    <Box component="h2" sx={{ ...sectionTitleSx, mb: 0.5 }}>
                        {t('commissionGroups.columns.commissionMatrix')}
                    </Box>
                    <Box
                        component="h3"
                        sx={{
                            m: 0,
                            fontSize: '1rem',
                            lineHeight: 1.4,
                            fontWeight: 500,
                            color: 'text.secondary',
                        }}
                    >
                        {t('commissionGroups.commissionValuesTitle')}
                    </Box>
                </Box>
                <Divider />
                <Box sx={sectionBodySx}>
                    <Alert
                        severity="info"
                        sx={{
                            mb: 2,
                            textAlign: 'left',
                            alignItems: 'flex-start',
                            '& .MuiAlert-message': { textAlign: 'left' },
                        }}
                    >
                        {t('commissionGroups.commissionValuesInfo')}
                    </Alert>
                    <TableContainer sx={tableContainerSx}>
                        <Table stickyHeader size="small" sx={tableSx}>
                            <TableHead sx={tableHeadSx}>
                                <TableRow sx={{ bgcolor: 'grey.50' }}>
                                    <TableCell sx={firstHeaderCellSx}>
                                        {t('tickettypes.ticketType')}
                                    </TableCell>
                                    {ticketOptions.map((option) => (
                                        <TableCell
                                            key={option.id}
                                            sx={optionHeaderCellSx(option, ticketOptions, {
                                                width: '25%',
                                            })}
                                        >
                                            {option.name}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {ticketTypes.map((type) => (
                                    <TableRow key={type.id} sx={bodyRowSx}>
                                        <TableCell sx={typeLabelCellSx}>{type.name}</TableCell>
                                        {ticketOptions.map((option) => (
                                            <TableCell
                                                key={option.id}
                                                sx={{ py: 1.5, px: 1, minWidth: 132 }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'stretch',
                                                        borderRadius: 1,
                                                        border: '1px solid',
                                                        borderColor: '#cbd5e1',
                                                        overflow: 'hidden',
                                                        bgcolor: 'common.white',
                                                    }}
                                                >
                                                    <Select
                                                        native
                                                        IconComponent={KeyboardArrowDownRoundedIcon}
                                                        size="small"
                                                        value={getType(type.id, option)}
                                                        onChange={(e) =>
                                                            handleTypeChange(
                                                                type.id,
                                                                option,
                                                                e.target.value,
                                                            )
                                                        }
                                                        sx={typeSelectSx}
                                                    >
                                                        <option value="percentage">%</option>
                                                        <option value="fixed">$</option>
                                                    </Select>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        inputProps={{
                                                            min: 0,
                                                            step: 0.01,
                                                            style: { textAlign: 'left' },
                                                        }}
                                                        value={getValue(type.id, option)}
                                                        onChange={(e) =>
                                                            handleValueChange(
                                                                type.id,
                                                                option,
                                                                e.target.value,
                                                            )
                                                        }
                                                        sx={valueFieldSx}
                                                    />
                                                </Box>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Paper>

            {/* Sell Permissions */}
            <Paper variant="outlined" sx={paperSx}>
                <Box sx={{ px: 2, py: 1.5, textAlign: 'left' }}>
                    <Box component="h2" sx={sectionTitleSx}>
                        {t('commissionGroups.sellPermissionsTitle')}
                    </Box>
                </Box>
                <Divider />
                <Box sx={sectionBodySx}>
                    <Alert
                        severity="info"
                        sx={{
                            mb: 2,
                            textAlign: 'left',
                            alignItems: 'flex-start',
                            '& .MuiAlert-message': { textAlign: 'left' },
                        }}
                    >
                        <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                            {t('commissionGroups.sellPermissionsTitle')}
                        </Box>
                        <Box>{t('commissionGroups.sellPermissionsInfo')}</Box>
                        <Box>{t('commissionGroups.guestVisibleLockedInfo')}</Box>
                    </Alert>
                    <TableContainer sx={tableContainerSx}>
                        <Table stickyHeader size="small" sx={tableSx}>
                            <TableHead sx={tableHeadSx}>
                                <TableRow sx={{ bgcolor: 'grey.50' }}>
                                    <TableCell sx={firstHeaderCellSx}>
                                        {t('tickettypes.ticketType')}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            py: 2,
                                            pr: 2,
                                            pl: 0,
                                            textAlign: 'left',
                                            fontSize: 13,
                                            fontWeight: 600,
                                            color: 'grey.900',
                                            width: '16.66%',
                                        }}
                                    >
                                        {t('commissionGroups.allOptions')}
                                    </TableCell>
                                    {ticketOptions.map((option) => (
                                        <TableCell
                                            key={option.id}
                                            sx={optionHeaderCellSx(option, ticketOptions, {
                                                py: 2,
                                                pr: 2,
                                                pl: 0,
                                                width: '16.66%',
                                            })}
                                        >
                                            {option.name}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {ticketTypes.map((type) => {
                                    const rowState = getRowEnabledState(type.id, type);
                                    return (
                                        <TableRow key={type.id} sx={bodyRowSx}>
                                            <TableCell sx={typeLabelCellSx}>{type.name}</TableCell>
                                            <TableCell sx={{ pl: 0 }}>
                                                <Checkbox
                                                    checked={rowState.checked}
                                                    indeterminate={rowState.indeterminate}
                                                    disabled={rowState.disabled}
                                                    onChange={(e) =>
                                                        handleRowEnabledChange(
                                                            type,
                                                            e.target.checked,
                                                        )
                                                    }
                                                    size="small"
                                                    sx={
                                                        rowState.disabled
                                                            ? guestVisibleCheckboxSx
                                                            : sellCheckboxSx
                                                    }
                                                />
                                            </TableCell>
                                            {ticketOptions.map((option) => {
                                                const locked = isCellGuestVisibleLocked(
                                                    type,
                                                    option,
                                                );
                                                return (
                                                    <TableCell key={option.id} sx={{ pl: 0 }}>
                                                        <Checkbox
                                                            checked={isSellEnabled(
                                                                type.id,
                                                                type,
                                                                option,
                                                            )}
                                                            disabled={locked}
                                                            onChange={(e) =>
                                                                handleEnabledChange(
                                                                    type.id,
                                                                    option,
                                                                    e.target.checked,
                                                                )
                                                            }
                                                            size="small"
                                                            sx={
                                                                locked
                                                                    ? guestVisibleCheckboxSx
                                                                    : sellCheckboxSx
                                                            }
                                                        />
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Paper>
        </Box>
    );
};

export default CommissionMatrix;
